import React from "react";
//#region redux
//#endregion
//#region types
import {
  ChoiceProps,
  CurrencyInputProps,
  NumberInputProps,
  SelectInputProps,
  SelectionChipsProps,
  TextInputProps,
} from "models/propTypes/wintFormPropsTypes";
import { TranslationKeys } from "translations";
import { CustomerConstants, FeatureConstants, IconDefinitionConstants } from "utils/constants";
import { ReminderSettingsSectionProps } from "models/propTypes/customerPropTypes";
import { CustomerEnums } from "models/enums";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import {
  HorizontalFormField,
  GroupSection,
  FormFieldLayout,
  VerticalFormField,
  TextInput,
  TextAreaInput,
  NumberInput,
  Choice,
  CurrencyInput,
  SelectInput,
  SelectionChips,
} from "components/common/wintForms";
import { useCustomerService, useFeatureService } from "serviceHooks";
import { Grid } from "@mui/material";
import { ActionLinkButton, Icon } from "components/common";
import FromStandardIcon from "./fromStandardIcon";
import InheritedOrOverriddenText from "./inheritedOrOverridenText";
//#endregion
import { EnumHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const ReminderSettingsSection: React.FC<ReminderSettingsSectionProps> = ({ connect, invoiceSettings }) => {
  const customerService = useCustomerService();
  const featureService = useFeatureService();

  const [isAutomaticRemindersFeatureEnabled, setIsAutomaticRemindersFeatureEnabled] = React.useState(false);

  React.useEffect(() => {
    featureService.hasFeature(FeatureConstants.AutomaticReminders).then((res) => {
      setIsAutomaticRemindersFeatureEnabled(res);
    });
  }, []);

  const settingTypeProps = connect("reminderSettings.settingType");
  const isCustomerSpecificSettings = settingTypeProps.value === CustomerEnums.CustomerSettingType.CustomerSpecific;

  const reminderSendTypeProps = connect("reminderSettings.reminderSendType");
  const connectReminderCost = connect("reminderSettings.reminderCost");
  const connectPenaltyInterestType = connect("reminderSettings.penaltyInterestType");
  const connectPenaltyInterestPercentage = connect("reminderSettings.penaltyInterestPercentage");
  const connectIsAutomaticRemindersEnabled = connect("reminderSettings.isAutomaticRemindersEnabled");
  const connectAutomaticReminderAmountLimit = connect("reminderSettings.automaticReminderAmountLimit");
  const connectAutomaticReminderDelay = connect("reminderSettings.automaticReminderDelay");
  const connectAutomaticReminderPeriod = connect("reminderSettings.automaticReminderPeriod");
  const connectAutomaticReminderCountLimit = connect("reminderSettings.automaticReminderCountLimit");
  const connectInvoiceReminderNotesTemplate = connect("reminderSettings.invoiceReminderNotesTemplate");
  const connectInvoiceReminderTitleTemplate = connect("reminderSettings.invoiceReminderTitleTemplate");
  const connectInvoiceReminderMessageTemplate = connect("reminderSettings.invoiceReminderMessageTemplate");
  const connectInvoiceReminderFileNameTemplate = connect("reminderSettings.invoiceReminderFileNameTemplate");
  const connectSendToType = connect("reminderSettings.invoiceReminderSendTo");

  return (
    <GroupSection
      header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.Reminders)}
      icon={IconDefinitionConstants.WBell}
      navKey={CustomerConstants.SettingsNavKeys.Reminders}
    >
      <HorizontalFormField<ChoiceProps>
        Component={Choice}
        {...settingTypeProps}
        label={TranslationKeys.Customer.ReminderSending}
        descriptionText={
          <Grid container direction="column" alignItems="start" className={cssExports.formControlDescription}>
            <Grid item>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Customer.InvoiceSendingDescription)}
            </Grid>
            <ActionLinkButton
              endIcon={<Icon icon={IconDefinitionConstants.WArrowSmallRight} />}
              className={cssExports.descriptionLink}
              href={UrlHelper.formatPath(`/Settings?section=invoice`)}
            >
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Customer.GoToStandardSettings)}
            </ActionLinkButton>
          </Grid>
        }
        items={customerService.getSettingTypes()}
      />
      {isCustomerSpecificSettings && (
        <>
          <HorizontalFormField<NumberInputProps>
            Component={NumberInput}
            {...connectReminderCost}
            label={TranslationKeys.Settings.ReminderCost}
            descriptionText={TranslationKeys.Settings.ReminderCostDescription}
            endAdornment={<FromStandardIcon value={connectReminderCost.value} />}
            fieldValueDynamicDescriptionText={
              <InheritedOrOverriddenText
                standardValue={invoiceSettings?.reminderCost}
                value={connectReminderCost.value}
              />
            }
            optional
          />
          <HorizontalFormField<SelectionChipsProps>
            Component={SelectionChips}
            {...connectPenaltyInterestType}
            label={TranslationKeys.Settings.PenaltyInterestType}
            descriptionText={TranslationKeys.Settings.PenaltyInterestTypeDescription}
            items={EnumHelper.getNumericItemValues(WintSuperkollAPIModel.InvoicePenaltyInterestType).map((state) => ({
              id: state,
              value: LocalizationHelperInstance.getFormattedMessage(`Settings.PenaltyInterestType_${state}`),
            }))}
            endAdornment={<FromStandardIcon value={connectPenaltyInterestType.value} />}
            fieldValueDynamicDescriptionText={
              <InheritedOrOverriddenText
                standardValue={
                  invoiceSettings?.penaltyInterest?.type !== undefined
                    ? LocalizationHelperInstance.getFormattedMessage(
                        `Settings.PenaltyInterestType_${invoiceSettings.penaltyInterest.type}`
                      )
                    : ""
                }
                value={connectPenaltyInterestType.value}
              />
            }
            optional
          />
          {connectPenaltyInterestType.value === WintSuperkollAPIModel.InvoicePenaltyInterestType.Prearranged && (
            <HorizontalFormField<NumberInputProps>
              Component={NumberInput}
              {...connectPenaltyInterestPercentage}
              label={TranslationKeys.Settings.InterestRate}
              descriptionText={TranslationKeys.Settings.InterestRateDescription}
              decimalPoints={2}
              suffix={"%"}
              maxNumber={100}
              optional
              endAdornment={<FromStandardIcon value={connectPenaltyInterestPercentage.value} />}
              fieldValueDynamicDescriptionText={
                <InheritedOrOverriddenText
                  standardValue={`${(invoiceSettings?.penaltyInterest?.rate ?? 0) * 100}%`}
                  value={connectPenaltyInterestPercentage.value}
                />
              }
            />
          )}
          {isAutomaticRemindersFeatureEnabled && (
            <>
              <HorizontalFormField<SelectionChipsProps>
                Component={SelectionChips}
                {...connectIsAutomaticRemindersEnabled}
                label={TranslationKeys.Invoice.ReminderManagement}
                descriptionText={TranslationKeys.Invoice.ReminderManagementDescription}
                endAdornment={<FromStandardIcon value={connectIsAutomaticRemindersEnabled.value} />}
                fieldValueDynamicDescriptionText={
                  <InheritedOrOverriddenText
                    standardValue={
                      invoiceSettings?.isAutomaticRemindersEnabled
                        ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.AutoReminder)
                        : LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ManualReminder)
                    }
                    value={connectIsAutomaticRemindersEnabled.value}
                  />
                }
                items={[
                  {
                    id: 0,
                    value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ManualReminder),
                  },
                  {
                    id: 1,
                    value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.AutoReminder),
                  },
                ]}
              />
              <HorizontalFormField<CurrencyInputProps>
                Component={CurrencyInput}
                {...connectAutomaticReminderAmountLimit}
                controlLabel={TranslationKeys.Settings.AutomaticReminderAmountLimitControlLabel}
                label={TranslationKeys.Settings.AutomaticReminderAmountLimit}
                descriptionText={TranslationKeys.Settings.AutomaticReminderAmountLimitDescription}
                endAdornment={<FromStandardIcon value={connectAutomaticReminderAmountLimit.value} />}
                fieldValueDynamicDescriptionText={
                  <InheritedOrOverriddenText
                    standardValue={invoiceSettings?.automaticReminderAmountLimit}
                    value={connectAutomaticReminderAmountLimit.value}
                  />
                }
                optional
              />
              <HorizontalFormField<NumberInputProps>
                Component={NumberInput}
                {...connectAutomaticReminderDelay}
                label={TranslationKeys.Settings.AutomaticReminderDelay}
                descriptionText={TranslationKeys.Settings.AutomaticReminderDelayDescription}
                controlLabel={TranslationKeys.Settings.AutomaticReminderDelayControlLabel}
                endAdornment={<FromStandardIcon value={connectAutomaticReminderDelay.value} />}
                fieldValueDynamicDescriptionText={
                  <InheritedOrOverriddenText
                    standardValue={invoiceSettings?.automaticReminderDelay}
                    value={connectAutomaticReminderDelay.value}
                  />
                }
                optional
              />
              <HorizontalFormField<NumberInputProps>
                Component={NumberInput}
                {...connectAutomaticReminderPeriod}
                label={TranslationKeys.Settings.AutomaticReminderPeriod}
                descriptionText={TranslationKeys.Settings.AutomaticReminderPeriodDescription}
                controlLabel={TranslationKeys.Settings.AutomaticReminderPeriodControlLabel}
                endAdornment={<FromStandardIcon value={connectAutomaticReminderPeriod.value} />}
                fieldValueDynamicDescriptionText={
                  <InheritedOrOverriddenText
                    standardValue={invoiceSettings?.automaticReminderPeriod}
                    value={connectAutomaticReminderPeriod.value}
                  />
                }
                optional
              />
              <HorizontalFormField<NumberInputProps>
                Component={NumberInput}
                {...connectAutomaticReminderCountLimit}
                label={TranslationKeys.Settings.AutomaticReminderCountLimit}
                descriptionText={TranslationKeys.Settings.AutomaticReminderCountLimitDescription}
                controlLabel={TranslationKeys.Settings.AutomaticReminderCountLimitControlLabel}
                endAdornment={<FromStandardIcon value={connectAutomaticReminderCountLimit.value} />}
                fieldValueDynamicDescriptionText={
                  <InheritedOrOverriddenText
                    standardValue={invoiceSettings?.automaticReminderCountLimit}
                    value={connectAutomaticReminderCountLimit.value}
                  />
                }
                optional
              />
            </>
          )}
          <HorizontalFormField<TextInputProps>
            Component={TextAreaInput}
            {...connectInvoiceReminderNotesTemplate}
            label={TranslationKeys.Settings.InvoiceReminderNotesTemplate}
            descriptionText={TranslationKeys.Settings.InvoiceReminderNotesTemplateDescription}
            endAdornment={<FromStandardIcon value={connectInvoiceReminderNotesTemplate.value} />}
            fieldValueDynamicDescriptionText={
              <InheritedOrOverriddenText
                standardValue={invoiceSettings?.defaultReminderNotes}
                value={connectInvoiceReminderNotesTemplate.value}
              />
            }
            optional
          />
          <HorizontalFormField<SelectInputProps>
            Component={SelectInput}
            {...reminderSendTypeProps}
            label={TranslationKeys.Customer.InvoiceReminderDeliveryMethod}
            descriptionText={TranslationKeys.Customer.InvoiceReminderDeliveryMethodDescription}
            items={EnumHelper.getNumericItemValues(WintSuperkollAPIModel.InvoiceSendType).map((key) => ({
              value: key,
              label: LocalizationHelperInstance.getFormattedMessage(`Invoice.AutomaticReminderSendType_${key}`),
            }))}
            endAdornment={<FromStandardIcon value={reminderSendTypeProps.value} />}
            fieldValueDynamicDescriptionText={
              <InheritedOrOverriddenText
                standardValue={
                  invoiceSettings?.defaultInvoiceReminderSendOption !== undefined
                    ? LocalizationHelperInstance.getFormattedMessage(
                        `Invoice.AutomaticReminderSendType_${invoiceSettings.defaultInvoiceReminderSendOption}`
                      )
                    : undefined
                }
                value={reminderSendTypeProps.value}
              />
            }
            optional
          />
          {reminderSendTypeProps.value === WintSuperkollAPIModel.InvoiceSendMethod.Email && (
            <>
              <FormFieldLayout direction="horizontal" label={TranslationKeys.Settings.InvoiceReminderReminderRecipient}>
                <Grid container direction="column" rowGap={1}>
                  <VerticalFormField<SelectInputProps>
                    Component={SelectInput}
                    {...connectSendToType}
                    label={TranslationKeys.Settings.InvoiceReminderReminderRecipient}
                    items={
                      EnumHelper.getNumericItemValues(WintSuperkollAPIModel.SendInvoiceMailsToEnum).map((item) => ({
                        value: item,
                        label: LocalizationHelperInstance.getTranslatedEnumValue(
                          "Invoice.SendInvoiceMailsTo",
                          WintSuperkollAPIModel.SendInvoiceMailsToEnum[item]
                        ),
                      })) ?? []
                    }
                    optional
                    noOuterSpacing
                  />
                  {connectSendToType.value === WintSuperkollAPIModel.SendInvoiceMailsToEnum.Other && (
                    <VerticalFormField<TextInputProps>
                      Component={TextInput}
                      {...connect("reminderSettings.invoiceReminderSendToEmail")}
                      label={TranslationKeys.Customer.InvoiceCustomSendToEmail}
                      noOuterSpacing
                    />
                  )}
                </Grid>
              </FormFieldLayout>
            </>
          )}
          <FormFieldLayout
                direction="horizontal"
                label={TranslationKeys.Settings.InvoiceReminderMail}
                descriptionText={TranslationKeys.Settings.InvoiceReminderMailDescription}
              >
                <Grid container direction="column" rowGap={2}>
                  <VerticalFormField<TextInputProps>
                    Component={TextInput}
                    {...connectInvoiceReminderTitleTemplate}
                    label={TranslationKeys.Common.Subject}
                    noOuterSpacing
                    endAdornment={<FromStandardIcon value={connectInvoiceReminderTitleTemplate.value} />}
                    fieldValueDynamicDescriptionText={
                      <InheritedOrOverriddenText
                        standardValue={invoiceSettings?.invoiceReminderTitleTemplate}
                        value={connectInvoiceReminderTitleTemplate.value}
                      />
                    }
                    optional
                  />
                  <VerticalFormField<TextInputProps>
                    Component={TextAreaInput}
                    {...connectInvoiceReminderMessageTemplate}
                    label={TranslationKeys.Common.Message}
                    maxRows={5}
                    minRows={2}
                    noOuterSpacing
                    endAdornment={<FromStandardIcon value={connectInvoiceReminderMessageTemplate.value} />}
                    fieldValueDynamicDescriptionText={
                      <InheritedOrOverriddenText
                        standardValue={invoiceSettings?.invoiceReminderMessageTemplate}
                        value={connectInvoiceReminderMessageTemplate.value}
                      />
                    }
                    optional
                  />
                </Grid>
              </FormFieldLayout>
              <HorizontalFormField<TextInputProps>
                Component={TextInput}
                {...connectInvoiceReminderFileNameTemplate}
                label={TranslationKeys.Settings.InvoiceReminderFileNameTemplate}
                descriptionText={TranslationKeys.Settings.InvoiceReminderFileNameTemplateDescription}
                endAdornment={<FromStandardIcon value={connectInvoiceReminderFileNameTemplate.value} />}
                fieldValueDynamicDescriptionText={
                  <InheritedOrOverriddenText
                    standardValue={invoiceSettings?.invoiceReminderFileNameTemplate}
                    value={connectInvoiceReminderFileNameTemplate.value}
                  />
                }
                optional
              />
        </>
      )}
    </GroupSection>
  );
};

export default ReminderSettingsSection;
